import React, { useEffect, useState } from "react";
import useMaskedInputState from "../../hooks/useMaskedInputState";
import { useVideo, useDeleteVideo, useSaveVideo } from "./hooks";

import { changeThumbnail } from "./helpers";

import {
  RightSidebar,
  RightSidebarHeader,
  RightSidebarSaveFooter,
  RightSidebarContent
} from "../RightSidebar";
import VideoDetails from "./VideoDetails";
import { Spinner, Button, Alert, Callout, Intent } from "@blueprintjs/core";
import { Icon } from "../../atoms/Icon";

import styles from "./VideoDetailSidebar.module.scss";

function VideoDetailSidebar({
  video,
  sidebarIsOpen = true,
  onDismiss,
  refetchVideos
}) {
  const [name, setName] = useMaskedInputState("", {
    regex: /[^ ]/,
    maxLength: 200
  });
  const [error, setError] = useState();
  const [videoDate, setVideoDate] = useState();
  const [thumbnail, setThumbnail] = useState();
  const [thumbnailFile, setThumbnailFile] = useState();
  const onThumbnailChange = changeThumbnail({ setThumbnail, setThumbnailFile });
  
  const isScheduled = video && video.scheduleStatus === "Enabled";

  const {
    isDeleting,
    isDeleteDisabled,
    isAlertOpen,
    setIsDeleting,
    setIsAlertOpen,
    onDeleteConfirmed,
    onDeletePressed,
    onAlertCancel
  } = useDeleteVideo({
    video,
    onDismiss,
    refetchVideos,
    setError
  });

  const { isSaving, isSaveDisabled, setIsSaving, onSave } = useSaveVideo({
    error,
    name,
    video,
    thumbnail,
    thumbnailFile,
    videoDate,
    setError,
    onDismiss,
    refetchVideos
  });

  useEffect(() => {
    if (!sidebarIsOpen) {
      setName("");
      setError();
      setIsSaving();
      setIsDeleting();
      setIsAlertOpen();
      setThumbnail();
      setThumbnailFile();
      setVideoDate();
    }
    else{
      setName(video && video.name)
    }
  }, [sidebarIsOpen]);

  return (
    <>
      <Alert
        className={styles.confirmAlert}
        cancelButtonText="Cancel"
        confirmButtonText="Yes, delete it"
        icon={
          <Icon
            name="warning-sign"
            color="#7a869b"
            size={32}
            className={styles.confirmAlertIcon}
          />
        }
        intent="danger"
        isOpen={isAlertOpen}
        onCancel={onAlertCancel}
        onConfirm={onDeleteConfirmed}
        canEscapeKeyCancel
        canOutsideClickCancel
      >
        <p className={styles.confirmAlertMessage}>
          Are you sure you want to delete this video? There is no going back.
        </p>
      </Alert>
      <RightSidebar sidebarIsOpen={sidebarIsOpen}>
        <RightSidebarHeader
          title={
            isScheduled ? "Edit Scheduled Broadcast" : "Edit Video Details"
          }
          onDismiss={onDismiss}
          isDismissDisabled={false}
        />
        <RightSidebarContent>
          {error && (
            <Callout
              intent={Intent.WARNING}
              title={error.title}
              style={{ marginBottom: "1em" }}
              icon={null}
            >
              {error.message}
            </Callout>
          )}
          {!video && !error && <Spinner />}
          {video && (
            <VideoDetails
              thumbnail={thumbnail || (video && video.thumbnailUrl)}
              onThumbnailChange={onThumbnailChange}
              name={name}
              videoScheduledDate={video && video.scheduleStartTime}
              isScheduled={isScheduled}
              onNameChange={setName}
              onVideoDateChange={setVideoDate}
            />
          )}
        </RightSidebarContent>
        <RightSidebarSaveFooter
          leftButton={
            <Button
              text="Delete Video"
              onClick={onDeletePressed}
              className="narrow warning"
              loading={isDeleting}
              disabled={isSaving || isDeleteDisabled}
              intent={Intent.DANGER}
            />
          }
          onCancel={onDismiss}
          onSave={onSave}
          pageIsLoading={isSaving}
          disableSave={isSaveDisabled}
        />
      </RightSidebar>
    </>
  );
}

export default VideoDetailSidebar;
